import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { T, Icon, stitchDialogPaper, stitchGradientBtn, stitchOutlineBtn } from '../ProjectOverview/parts/stitch';
import { mailtoLink } from './briefingMail';

/**
 * Resultaat van een generator (markdown) met kopieer + bewaar-als-notitie —
 * gebruikt door de opstart-wizard; StagePanel heeft dezelfde acties inline.
 *
 * Bij de briefing-check komt er `klantMail` ({onderwerp, tekst}) mee: de vragen voor
 * de klant als kant-en-klare mail. Die verschijnt eerst in een bewerkbare preview —
 * de PL leest en past aan — en pas daarna kopiëren, of openen in het mailprogramma
 * als het adres van de contactpersoon bekend is (`klantEmail`). Codex 28 sep: de
 * tekst komt deels van een AI die een klantdocument las; niets gaat ongezien weg.
 */
function GenResultDialog({ result, onClose, projectRecId }) {
  // "Sluiten" gooide het gegenereerde document weg zonder dat dat ergens stond —
  // uit de feedback: "als ik op sluiten druk, wordt het dan opgeslagen? Dit is me
  // onduidelijk." Nu zegt het venster wat er gebeurt, en na bewaren verandert de knop.
  const [bewaard, setBewaard] = useState(false);
  // Bewerkbare mail-preview; null = dicht.
  const [mail, setMail] = useState(null);
  useEffect(() => { setBewaard(false); setMail(null); }, [result]);

  const bewaarAlsNotitie = async () => {
    if (!result?.markdown) return;
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/project-notes`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId: projectRecId,
          title: (result.titel || 'Gegenereerd document').slice(0, 50),
          notes: result.markdown,
          source: 'Manual',
        }),
      });
      if (!r.ok) throw new Error('Opslaan mislukt');
      setBewaard(true);
      toast.success('Bewaard als projectnotitie — terug te vinden onder Notities bij het project');
    } catch (e) { toast.error(e.message); }
  };

  const klantMail = result?.klantMail || null;
  const mailto = mail ? mailtoLink(result?.klantEmail, mail) : null;
  const kopieerMail = () => {
    if (!mail) return;
    navigator.clipboard?.writeText(`Onderwerp: ${mail.onderwerp}\n\n${mail.tekst}`);
    toast.success('Mail aan de klant gekopieerd — onderwerp staat bovenaan');
  };

  return (
    <Dialog open={!!result} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: stitchDialogPaper }}>
      <DialogTitle sx={{ fontFamily: T.font, fontWeight: 800 }}>{result?.titel || 'Gegenereerd document'}</DialogTitle>
      <DialogContent dividers>
        {result?.hint && (
          <Typography role="note" sx={{ fontFamily: T.fontBody, fontSize: '0.8rem', color: T.secondary, mb: 1.5 }}>
            {result.hint}
            {typeof result.opnieuw === 'function' && (
              <Box component="span" sx={{ ...stitchOutlineBtn, ml: 1, py: 0.3, fontSize: '0.75rem' }} onClick={result.opnieuw}>
                <Icon name="auto_awesome" size={14} /> Opnieuw checken
              </Box>
            )}
          </Typography>
        )}
        <Box sx={{
          fontFamily: T.fontBody, fontSize: '0.875rem', color: T.text, lineHeight: 1.7,
          '& p': { m: 0, mb: 1 },
          '& p:last-child': { mb: 0 },
          '& ul, & ol': { my: 0.5, pl: 2.5 },
          '& li': { mb: 0.4 },
          '& h1, & h2, & h3, & h4': {
            fontFamily: T.font, fontWeight: 800, color: T.text,
            fontSize: '1rem', mt: 2, mb: 0.75,
          },
          '& h1:first-of-type, & h2:first-of-type, & h3:first-of-type': { mt: 0 },
          '& strong': { fontWeight: 700 },
          '& hr': { border: 0, borderTop: `1px solid ${T.surfaceHigh}`, my: 1.5 },
        }}>
          <ReactMarkdown>{result?.markdown || ''}</ReactMarkdown>
        </Box>
        {mail && (
          <Box sx={{ mt: 2.5, pt: 2, borderTop: `1px solid ${T.surfaceHigh}`, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <Typography sx={{ fontFamily: T.font, fontWeight: 800, fontSize: '0.95rem', color: T.text }}>
              Mail aan de klant — lees en pas aan vóór je hem verstuurt
            </Typography>
            <Typography role="note" sx={{ fontFamily: T.fontBody, fontSize: '0.8rem', fontWeight: 700, color: T.secondary }}>
              Controleer vóór versturen: geen interne info, bedragen of toezeggingen.
            </Typography>
            {klantMail?.notitie && (
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.8rem', color: T.outline }}>
                {klantMail.notitie}
              </Typography>
            )}
            <TextField size="small" label="Onderwerp" value={mail.onderwerp}
              onChange={(e) => setMail((m) => ({ ...m, onderwerp: e.target.value }))} fullWidth />
            <TextField size="small" label="Tekst" value={mail.tekst} multiline minRows={8}
              inputProps={{ 'aria-label': 'Mailtekst aan de klant' }}
              onChange={(e) => setMail((m) => ({ ...m, tekst: e.target.value }))} fullWidth />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Box component="span" sx={stitchOutlineBtn} onClick={kopieerMail}>
                <Icon name="content_copy" size={16} /> Kopieer als mail aan klant
              </Box>
              {mailto && (
                <Box component="a" href={mailto} sx={{ ...stitchOutlineBtn, textDecoration: 'none' }}>
                  <Icon name="mail" size={16} /> Open in mail
                </Box>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: bewaard ? '#166534' : T.outline, flex: 1 }}>
          {bewaard
            ? '✓ Staat bij het project onder Notities.'
            : 'Dit venster bewaart niets vanzelf — sluiten gooit het document weg.'}
        </Typography>
        <Box component="span" sx={stitchOutlineBtn}
          onClick={() => { navigator.clipboard?.writeText(result?.markdown || ''); toast.success('Gekopieerd'); }}>
          <Icon name="content_copy" size={16} /> Kopieer
        </Box>
        {klantMail && !mail && (
          <Box component="span" sx={stitchOutlineBtn} onClick={() => setMail({ ...klantMail })}>
            <Icon name="forward_to_inbox" size={16} /> Mail aan klant opstellen
          </Box>
        )}
        {!bewaard && (
          <Box component="span" sx={stitchOutlineBtn} onClick={bewaarAlsNotitie}>
            <Icon name="note_add" size={16} /> Bewaar als notitie
          </Box>
        )}
        <Box component="span" onClick={onClose} sx={stitchGradientBtn}>
          {bewaard ? 'Sluiten' : 'Sluiten zonder bewaren'}
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default GenResultDialog;
